/**
 * Escolha de qualidade do VideoPlayer.
 *
 * O backend entrega varias renditions do mesmo arquivo (ver
 * medhunter-api/docs/followup-videos.md). Nao ha manifesto HLS/DASH, entao a
 * troca e feita no `src` do proprio `<video>` — a heuristica abaixo e o que
 * substitui o ABR que um player adaptativo faria sozinho.
 */

/**
 * Uma qualidade reproduzivel. Estruturalmente igual a `FollowupVideoSource` de
 * `types/api`, mas declarada aqui para o player nao depender de uma feature.
 */
export interface VideoSource {
  label: string
  width: number | null
  height: number | null
  content_type: string
  url: string
}

/** `'auto'` deixa a heuristica decidir; qualquer outro valor e um `label` de rendition. */
export type QualitySelection = 'auto' | string

/** Teto de altura por qualidade de conexao, para nao estourar dados em rede ruim. */
const CONNECTION_HEIGHT_CAP: Readonly<Record<string, number>> = {
  'slow-2g': 240,
  '2g': 360,
  '3g': 480,
}

interface NetworkInformation {
  effectiveType?: string
  saveData?: boolean
}

function networkHeightCap(): number {
  const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection

  if (!connection) return Number.POSITIVE_INFINITY
  if (connection.saveData) return 360

  const cap = connection.effectiveType ? CONNECTION_HEIGHT_CAP[connection.effectiveType] : undefined

  return cap ?? Number.POSITIVE_INFINITY
}

/** Altura util em pixels fisicos para um player com `renderedWidth` de largura CSS. */
function targetHeight(renderedWidth: number): number {
  const ratio = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1
  const width = renderedWidth > 0 ? renderedWidth : 720

  return Math.round(((width * 9) / 16) * ratio)
}

function byHeightAscending(sources: VideoSource[]): VideoSource[] {
  return [...sources].sort((a, b) => (a.height ?? 0) - (b.height ?? 0))
}

/**
 * Menor rendition que ainda cobre a area do player, respeitando o teto de rede.
 * Sem `height` em nenhuma fonte a heuristica nao tem o que comparar e devolve a
 * primeira da lista (o backend as entrega da menor para a maior).
 */
export function pickAutoSource(
  sources: VideoSource[],
  renderedWidth: number
): VideoSource | null {
  if (sources.length === 0) return null

  const measured = sources.filter((source) => source.height !== null)

  if (measured.length === 0) return sources[0]

  const ascending = byHeightAscending(measured)
  const ceiling = Math.min(targetHeight(renderedWidth), networkHeightCap())
  const withinCeiling = ascending.filter((source) => (source.height ?? 0) <= ceiling)

  // Acima do teto so sobra a menor rendition — melhor entregar ela do que nada.
  return withinCeiling.at(-1) ?? ascending[0]
}

/** Fonte efetiva para a selecao atual, com fallback para o comportamento automatico. */
export function resolveSource(
  sources: VideoSource[],
  selection: QualitySelection,
  renderedWidth: number
): VideoSource | null {
  if (selection === 'auto') return pickAutoSource(sources, renderedWidth)

  return sources.find((source) => source.label === selection) ?? pickAutoSource(sources, renderedWidth)
}
